import {
  AppBase,
  baseFromDoc,
  FirestoreBaseDoc,
  makeDocConverter,
} from '../base/firestore-model-base'
import { Timestamp, doc, getFirestore } from 'firebase/firestore'
import { parseDate, parseDateOptional, dateToJson } from '../../utils/date-util'
import type { ProjectStatus } from '../../utils/project-status'
import type { PriorContactVisitPlan } from '../../utils/prior-contact-visit-plan'
import type { ProjectHistorySummary } from './project-history-summary.modl'
import type { TownCompanyHistorySummary } from '../town_company/town-company-history-summary.modl'
import { getFirebaseApp } from '../../config/firebase.config'

/**
 * Project モデル
 *
 * - `ProjectDoc`: Firestore に保存される形（Timestamp のまま / snake_case）
 * - `ProjectEntity`: アプリで使う形（Date に変換 / snake_case）※ CRUD と UI 共通
 */

/** Firestoreに保存される形（Timestampのまま） */
export type ProjectDoc = FirestoreBaseDoc & {
  // 基本情報
  project_number?: string // プロジェクト番号（例: S-20250415-CF。登録時に1度だけ付与）
  name: string // 職場体験名
  status: ProjectStatus // ステータス（職場体験情報の登録 ～ 完了）
  /** 案件完了日時（status を completed にしたときに更新） */
  completed_at?: Timestamp | null
  /** 実施最終日の終了手続き案内メールを enqueue した日時 */
  project_end_procedure_due_mail_sent_at?: Timestamp | null
  /** フォーム短縮URLの4文字コード（案件作成時に設定。完了後も消さず残し、手動復活時に参照する） */
  form_short_code?: string | null

  // 日程情報
  start_date?: Timestamp // 開始日
  end_date?: Timestamp // 終了日
  min_experience_days?: number // 最低体験日数

  // 参加者情報
  student_grade?: string // 対象学年
  participant_count?: number // 参加生徒数
  min_participants_per_company?: number // 1事業所あたりの最小参加希望人数
  max_participants_per_company?: number // 1事業所あたりの最大参加希望人数

  // 時間情報
  start_time?: string // 開始時間（例: 09:00）
  end_time?: string // 終了時間（例: 17:00）
  min_experience_time?: number // 最低体験時間（分）
  lunch_notes?: string | null // 昼食についての連絡事項（オプショナル）

  // 連絡先情報
  contact_person_name?: string // はがきに記載する職場体験担当者名

  /** 事前連絡・訪問の実施予定（実施する／実施しない／未定） */
  prior_contact_visit_plan?: PriorContactVisitPlan | string | null
  /** 事前連絡・訪問の実施予定日（自由記述） */
  prior_contact_visit_schedule?: string | null
  /** 学校最寄り駅 */
  school_nearest_station?: string | null
  /** 希望する路線・駅と各駅からの所要時間 */
  preferred_route_and_travel_time?: string | null

  // その他
  notes?: string | null // 備考（オプショナル）
  /** 追加質問1（質問したい内容。未登録可） */
  optional_question_1?: string | null
  /** 追加質問2（質問したい内容。未登録可） */
  optional_question_2?: string | null
  /** 追加質問3（質問したい内容。未登録可） */
  optional_question_3?: string | null

  // 関連情報（path で string 管理）
  school_account?: string | null

  /** マネーフォワード クラウド請求書の請求書ID（請求書作成時に保存） */
  moneyforward_billing_id?: string | null
  /** マネーフォワード連携時の請求日（日本時間の YYYY-MM-DD） */
  moneyforward_billing_date?: string | null
  /** マネーフォワード連携時の支払い期限 */
  moneyforward_due_date?: string | null
  /** 請求済み日時（請求書作成時に設定） */
  billed_at?: Timestamp | null

  // 履歴概要（optional）
  history_summaries?: unknown[]
  town_company_history_summaries?: unknown[]

  // 旧 `ProjectService` 用（元の Project には無いが、現行 CRUD の互換用）
  description?: string
}

/** アプリで使う形（object / Date に変換・snake_case）※ UI 含め共通で利用 */
export type ProjectEntity = AppBase & {
  // 基本情報
  project_number?: string // プロジェクト番号（例: S-20250415-CF。登録時に1度だけ付与）
  name: string // 職場体験名
  status: ProjectStatus // ステータス（職場体験情報の登録 ～ 完了）
  /** 案件完了日時（status を completed にしたときに更新） */
  completed_at?: Date | null
  /** 実施最終日の終了手続き案内メールを enqueue した日時 */
  project_end_procedure_due_mail_sent_at?: Date | null
  /** フォーム短縮URLの4文字コード（案件作成時に設定。完了後も消さず残し、手動復活時に参照する） */
  form_short_code?: string | null

  // 日程情報
  start_date?: Date // 開始日
  end_date?: Date // 終了日
  min_experience_days?: number // 最低体験日数

  // 参加者情報
  student_grade?: string // 対象学年
  participant_count?: number // 参加生徒数
  min_participants_per_company?: number // 1事業所あたりの最小参加希望人数
  max_participants_per_company?: number // 1事業所あたりの最大参加希望人数

  // 時間情報
  start_time?: string // 開始時間（例: 09:00）
  end_time?: string // 終了時間（例: 17:00）
  min_experience_time?: number // 最低体験時間（分）
  lunch_notes?: string | null // 昼食についての連絡事項（オプショナル）

  // 連絡先情報
  contact_person_name?: string // はがきに記載する職場体験担当者名

  /** 事前連絡・訪問の実施予定 */
  prior_contact_visit_plan?: PriorContactVisitPlan | string | null
  /** 事前連絡・訪問の実施予定日（自由記述） */
  prior_contact_visit_schedule?: string | null
  /** 学校最寄り駅 */
  school_nearest_station?: string | null
  /** 希望する路線・駅と各駅からの所要時間 */
  preferred_route_and_travel_time?: string | null

  // その他
  notes?: string | null // 備考（オプショナル）
  /** 追加質問1（質問したい内容。未登録可） */
  optional_question_1?: string | null
  /** 追加質問2（質問したい内容。未登録可） */
  optional_question_2?: string | null
  /** 追加質問3（質問したい内容。未登録可） */
  optional_question_3?: string | null

  // 関連情報
  school_account?: string | null
  /** UI用: 学校アカウントID（school_account から抽出） */
  school_account_id?: string

  /** マネーフォワード クラウド請求書の請求書ID（請求書作成時に保存） */
  moneyforward_billing_id?: string | null
  /** マネーフォワード連携時の請求日（日本時間の YYYY-MM-DD） */
  moneyforward_billing_date?: string | null
  /** マネーフォワード連携時の支払い期限 */
  moneyforward_due_date?: string | null
  /** 請求済み日時（請求書作成時に設定） */
  billed_at?: Date | null

  // 履歴概要（UIで表示するため型付き）
  history_summaries?: ProjectHistorySummary[]
  town_company_history_summaries?: TownCompanyHistorySummary[]

  // 旧 `ProjectService` 用（互換）
  description?: string
}

export const projectDocConverter = makeDocConverter<ProjectDoc>()

/** 履歴概要のパース（Doc 用） */
function parseHistorySummaries(docData: ProjectDoc): ProjectHistorySummary[] | undefined {
  const raw = (docData as any).history_summaries
  if (!Array.isArray(raw)) return undefined
  return raw.map((summary: any) => {
    const cAt = parseDate(summary.created_at)
    return {
      summary: summary.summary,
      updatedByUserType: summary.updated_by_user_type,
      created_at: cAt,
      documentId: summary.document_reference
        ? (() => {
            const db = getFirestore(getFirebaseApp())
            const pathMatch = String(summary.document_reference).match(/\/documents\/(.+)$/)
            if (!pathMatch || !pathMatch[1]) return `history-${cAt.getTime()}`
            return doc(db, pathMatch[1]).id
          })()
        : `history-${cAt.getTime()}`,
    }
  })
}

/** 事業所履歴概要のパース（Doc 用） */
function parseTownCompanyHistorySummaries(docData: ProjectDoc): TownCompanyHistorySummary[] | undefined {
  const raw = (docData as any).town_company_history_summaries
  if (!Array.isArray(raw)) return undefined
  return raw.map((summary: any) => {
    const cAt = parseDate(summary.created_at)
    if (!summary.document_reference) {
      return { summary: summary.summary, updatedByUserType: summary.updated_by_user_type, created_at: cAt, documentId: null }
    }
    const db = getFirestore(getFirebaseApp())
    const pathMatch = String(summary.document_reference).match(/\/documents\/(.+)$/)
    return {
      summary: summary.summary,
      updatedByUserType: summary.updated_by_user_type,
      created_at: cAt,
      documentId: pathMatch && pathMatch[1] ? doc(db, pathMatch[1]).id : `town-company-${cAt.getTime()}`,
    }
  })
}

/** 既存データ互換: DocumentReference または string から path を抽出 */
function refToPath(v: unknown): string | null {
  return typeof v === 'string' ? v : (v as { path?: string; id?: string } | null)?.path ?? (v as { id?: string } | null)?.id ?? null
}

function formatJstDate(date: Date): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Tokyo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(date)
}

function parseStoredJstDate(value: unknown): string | null {
  if (!value) return null
  if (typeof value === 'string') return value
  const parsed = parseDateOptional(value)
  return parsed ? formatJstDate(parsed) : null
}

export function toProjectEntity(id: string, docData: ProjectDoc, path: string): ProjectEntity {
  const school_account = refToPath((docData as { school_account?: unknown }).school_account)
  const school_account_id = school_account?.split('/').pop() ?? undefined
  const base = baseFromDoc(id, docData, path)
  return {
    ...base,
    name: docData.name,
    project_number: (docData as any).project_number,
    status: docData.status,
    completed_at: parseDateOptional((docData as any).completed_at),
    project_end_procedure_due_mail_sent_at: parseDateOptional((docData as any).project_end_procedure_due_mail_sent_at),
    form_short_code: (docData as any).form_short_code ?? null,

    start_date: parseDateOptional((docData as any).start_date),
    end_date: parseDateOptional((docData as any).end_date),
    min_experience_days: (docData as any).min_experience_days,

    student_grade: (docData as any).student_grade,
    participant_count: (docData as any).participant_count,
    min_participants_per_company: (docData as any).min_participants_per_company,
    max_participants_per_company: (docData as any).max_participants_per_company,

    start_time: (docData as any).start_time,
    end_time: (docData as any).end_time,
    min_experience_time: (docData as any).min_experience_time,
    lunch_notes: (docData as any).lunch_notes ?? null,

    contact_person_name: (docData as any).contact_person_name,

    prior_contact_visit_plan: (docData as any).prior_contact_visit_plan ?? null,
    prior_contact_visit_schedule: (docData as any).prior_contact_visit_schedule ?? null,
    school_nearest_station: (docData as any).school_nearest_station ?? null,
    preferred_route_and_travel_time: (docData as any).preferred_route_and_travel_time ?? null,

    notes: (docData as any).notes ?? null,
    optional_question_1: (docData as any).optional_question_1 ?? null,
    optional_question_2: (docData as any).optional_question_2 ?? null,
    optional_question_3: (docData as any).optional_question_3 ?? null,

    school_account: (docData as any).school_account,
    school_account_id: school_account_id ?? undefined,

    moneyforward_billing_id: (docData as any).moneyforward_billing_id ?? null,
    moneyforward_billing_date: parseStoredJstDate((docData as any).moneyforward_billing_date),
    moneyforward_due_date: parseStoredJstDate((docData as any).moneyforward_due_date),
    billed_at: parseDateOptional((docData as any).billed_at),

    history_summaries: parseHistorySummaries(docData),
    town_company_history_summaries: parseTownCompanyHistorySummaries(docData),

    description: (docData as any).description,
  }
}

/** 履歴ドキュメント等へ保存するためのスナップショット（ProjectEntity → Firestore 用） */
export function toProjectSnapshotJson(project: ProjectEntity): Record<string, any> {
  return {
    project_number: project.project_number ?? '',
    name: project.name,
    status: project.status,
    completed_at: dateToJson(project.completed_at),
    project_end_procedure_due_mail_sent_at: dateToJson(project.project_end_procedure_due_mail_sent_at),
    form_short_code: project.form_short_code ?? null,
    start_date: dateToJson(project.start_date),
    end_date: dateToJson(project.end_date),
    min_experience_days: project.min_experience_days ?? 0,
    student_grade: project.student_grade ?? '',
    participant_count: project.participant_count ?? 0,
    min_participants_per_company: project.min_participants_per_company ?? 0,
    max_participants_per_company: project.max_participants_per_company ?? 0,
    start_time: project.start_time ?? '',
    end_time: project.end_time ?? '',
    min_experience_time: project.min_experience_time ?? 0,
    lunch_notes: project.lunch_notes ?? null,
    contact_person_name: project.contact_person_name ?? '',
    prior_contact_visit_plan: project.prior_contact_visit_plan ?? null,
    prior_contact_visit_schedule: project.prior_contact_visit_schedule ?? null,
    school_nearest_station: project.school_nearest_station ?? null,
    preferred_route_and_travel_time: project.preferred_route_and_travel_time ?? null,
    notes: project.notes ?? null,
    optional_question_1: project.optional_question_1 ?? null,
    optional_question_2: project.optional_question_2 ?? null,
    optional_question_3: project.optional_question_3 ?? null,
    school_account: project.school_account ?? null,
    moneyforward_billing_id: project.moneyforward_billing_id ?? null,
    moneyforward_billing_date: project.moneyforward_billing_date ?? null,
    moneyforward_due_date: project.moneyforward_due_date ?? null,
    billed_at: dateToJson(project.billed_at),
    created_at: dateToJson(project.created_at),
    updated_at: dateToJson(project.updated_at),
    is_deleted: project.is_deleted,
    document_id: project.id,
  }
}
